import React, { useState } from 'react';
import { useNavigate,Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import importImage from '../../utils/images/importImage';
import { showToast } from '../../utils/toast';
import { Eye, EyeOff } from 'lucide-react';

const bgImg = importImage("contact-bg.jpg");

const LoginForm = () => {
  const LoginFormImage = importImage('login.png');
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', role: 'user' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const { email, password } = formData;
    const newErrors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      newErrors.email = t('errors.emailRequired', 'Email is required.');
    } else if (!emailRegex.test(email)) {
      newErrors.email = t('errors.invalidEmail', 'Please enter a valid email address.');
    }

    if (!password.trim()) {
      newErrors.password = t('errors.passwordRequired', 'Password is required.');
    } else if (password.length < 6) {
      newErrors.password = t('errors.shortPassword', 'Password must be at least 6 characters long.');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await login(formData.email, formData.password, formData.role);
      const user = response?.data?.data?.user;
      const success = response?.data?.success;
      const message = response?.data?.message;

      if (success && user) {
        showToast('success', message || t('login.success', 'Login successful!'));
        const { first_time } = user;
        navigate(first_time === 'Yes' ? '/first-time-password-change' : '/role', { replace: true });
      }
    } catch (err) {
      showToast('error', err.message || t('login.error', 'Login failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page bg-light min-vh-100 d-flex align-items-center py-5"
    style={{
        backgroundImage: `url(${bgImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container">
        <div className="row g-0">
          {/* Combined Card - Form + Image */}
          <div className="col-12 col-lg-12">
            <div className="card shadow-lg border-0 overflow-hidden" style={{ 
              borderRadius: '20px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 15px 30px rgba(0,0,0,0.12)'
            }}>
              <div className="row g-0 h-100">
                {/* Form Column */}
                <div className="col-lg-5 d-flex align-items-center">
                  <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-4">
                      <h2 className="text-primary fw-bold">
                        {t('login.heading', 'Welcome Back')}
                      </h2>
                      <p className="text-muted">
                        {t('login.subheading', 'Sign in to continue to your account')}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                      <div className="mb-4">
                        <label htmlFor="email" className="form-label fw-semibold">
                          {t('login.usernameLabel', 'Email Address')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-envelope text-muted"></i>
                          </span>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            className={`form-control py-3 ${errors.email ? 'is-invalid' : ''}`}
                            placeholder={t('placeholders.email', 'Enter your email')}
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="password" className="form-label fw-semibold">
                          {t('login.passwordLabel', 'Password')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-lock text-muted"></i>
                          </span>
                          <input
                            id="password"
                            type={passwordVisible ? "text" : "password"}
                            name="password"
                            className={`form-control py-3 ${errors.password ? 'is-invalid' : ''}`}
                            placeholder={t('placeholders.password', 'Enter your password')}
                            value={formData.password}
                            onChange={handleChange}
                            required
                          />
                          <button
                            type="button"
                            className="input-group-text bg-light border-start-0"
                            onClick={togglePasswordVisibility}
                            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                          >
                            {passwordVisible ? (
                              <EyeOff size={18} className="text-muted" />
                            ) : (
                              <Eye size={18} className="text-muted" />
                            )}
                          </button>
                          {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="form-check">
                          <input type="checkbox" className="form-check-input" id="rememberMe" />
                          <label className="form-check-label" htmlFor="rememberMe">
                            {t('login.rememberMe', 'Remember me')}
                          </label>
                        </div>
                        <Link to="/forgot-password" className="text-primary text-decoration-none">
                          {t('login.forgotPassword', 'Forgot password?')}
                        </Link>
                      </div>

                      <div className="d-grid mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-3 fw-semibold"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('buttons.loggingIn', 'Logging in...')}
                            </>
                          ) : (
                            t('buttons.login', 'Login')
                          )}
                        </button>
                      </div>

                      <div className="text-center mt-4 pt-3 border-top">
                        <p className="mb-0">
                          {t('login.noAccount', "Don't have an account?")}{' '}
                          <Link to="/register" className="text-primary fw-semibold text-decoration-none">
                            {t('login.register', 'Register here')}
                          </Link>
                        </p>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Image Column */}
                <div className="col-lg-7 d-none d-lg-flex position-relative">
                  <div className="h-100 w-100" style={{
                    backgroundImage: `url(${LoginFormImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderLeft: '1px solid rgba(0,0,0,0.1)',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Creative overlay */}
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                      background: 'linear-gradient(135deg, rgba(13,110,253,0.15) 0%, rgba(255,255,255,0) 100%)'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;